import moment from 'moment';

/**
 * Centralized timezone utility for GMT+0 (UTC) handling
 * Ensures all date/time operations across the application use consistent GMT+0 timezone
 */

/**
 * Get today's date in GMT+0/UTC format (YYYY-MM-DD)
 * @returns Today's date in YYYY-MM-DD format based on UTC time
 */
export const getTodayGMT = (): string => {
  return moment.utc().format('YYYY-MM-DD');
};

/**
 * Get current date range for last N days in GMT+0
 * @param days Number of days to go back (default: 7)
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
export const getDateRangeGMT = (days: number = 7): { startDate: string; endDate: string } => {
  const endDate = moment.utc().format('YYYY-MM-DD');
  const startDate = moment.utc().subtract(days, 'days').format('YYYY-MM-DD');
  return { startDate, endDate };
};

/**
 * Check if a datetime is today in GMT+0/UTC
 * @param dateString ISO datetime string or any moment-parseable date
 * @returns true if the date is today in UTC
 */
export const isTodayGMT = (dateString: string | Date): boolean => {
  const date = moment.utc(dateString);
  const now = moment.utc();

  return (
    date.year() === now.year() &&
    date.month() === now.month() &&
    date.date() === now.date()
  );
};

/**
 * Format datetime to GMT time (HH:mm)
 * @param dateString ISO datetime string
 * @returns Formatted time in HH:mm format
 */
export const formatGMTTime = (dateString: string | Date): string => {
  return moment.utc(dateString).format('HH:mm');
};

/**
 * Format datetime to GMT date and time (YYYY-MM-DD HH:mm:ss)
 * @param dateString ISO datetime string
 * @returns Formatted datetime
 */
export const formatGMTDateTime = (dateString: string | Date): string => {
  return moment.utc(dateString).format('YYYY-MM-DD HH:mm:ss');
};

/**
 * Format datetime to local WIB/Indonesia time (HH:mm)
 * Converts from GMT+0 to WIB (GMT+7)
 * @param dateString ISO datetime string in GMT+0
 * @returns Formatted time in HH:mm format in WIB timezone
 */
export const formatWIBTime = (dateString: string | Date): string => {
  return moment.utc(dateString).utcOffset(7 * 60).format('HH:mm');
};

/**
 * Format datetime to local WIB/Indonesia date and time (YYYY-MM-DD HH:mm:ss)
 * Converts from GMT+0 to WIB (GMT+7)
 * @param dateString ISO datetime string in GMT+0
 * @returns Formatted datetime in WIB timezone
 */
export const formatWIBDateTime = (dateString: string | Date): string => {
  return moment.utc(dateString).utcOffset(7 * 60).format('YYYY-MM-DD HH:mm:ss');
};

/**
 * Format datetime as relative time (e.g., "2 hours ago")
 * @param dateString ISO datetime string
 * @returns Relative time string in English
 */
export const formatRelativeTime = (dateString: string | Date): string => {
  return moment.utc(dateString).fromNow();
};

/**
 * Get current UTC timestamp
 * @returns Current timestamp in milliseconds (UTC)
 */
export const getNowGMT = (): number => {
  return moment.utc().valueOf();
};

/**
 * Create a date range query for API calls (GMT+0)
 * @param startDate Optional start date (YYYY-MM-DD or ISO string)
 * @param endDate Optional end date (YYYY-MM-DD or ISO string)
 * @returns Object with startDate and endDate in YYYY-MM-DD format (GMT+0)
 */
export const createDateRangeQuery = (
  startDate?: string,
  endDate?: string
): { startDate: string; endDate: string } => {
  const today = getTodayGMT();

  return {
    startDate: startDate ? moment.utc(startDate).format('YYYY-MM-DD') : today,
    endDate: endDate ? moment.utc(endDate).format('YYYY-MM-DD') : today,
  };
};

/**
 * Check if two dates are the same day in GMT+0
 * @param date1 First date
 * @param date2 Second date
 * @returns true if both dates are the same day in UTC
 */
export const isSameDayGMT = (date1: string | Date, date2: string | Date): boolean => {
  const d1 = moment.utc(date1);
  const d2 = moment.utc(date2);

  return (
    d1.year() === d2.year() &&
    d1.month() === d2.month() &&
    d1.date() === d2.date()
  );
};

/**
 * Get GMT+0 date one day ago
 * @returns Yesterday's date in YYYY-MM-DD format (GMT+0)
 */
export const getYesterdayGMT = (): string => {
  return moment.utc().subtract(1, 'day').format('YYYY-MM-DD');
};

/**
 * Format a date range for display
 * @param startDate YYYY-MM-DD format
 * @param endDate YYYY-MM-DD format
 * @returns Formatted string like "Jan 1 - Jan 31, 2024"
 */
export const formatDateRange = (startDate: string, endDate: string): string => {
  const start = moment.utc(startDate);
  const end = moment.utc(endDate);

  if (start.isSame(end, 'day')) {
    return start.format('MMM DD, YYYY');
  }

  if (start.isSame(end, 'year')) {
    return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
  }

  return `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`;
};

/**
 * Convert browser local date string to GMT+0 for API calls
 * Useful when user selects a date in date picker (which uses browser's local timezone)
 * @param localDateString Date string from date input (YYYY-MM-DD)
 * @returns Same date in GMT+0 format (YYYY-MM-DD)
 */
export const localDateToGMT = (localDateString: string): string => {
  // Parse as local date, then get the UTC date
  return moment(localDateString, 'YYYY-MM-DD').utc().format('YYYY-MM-DD');
};

/**
 * Format date as "day, date" (e.g., "Monday, 1 Dec 2025")
 * @param dateString YYYY-MM-DD format or ISO datetime string
 * @returns Formatted string like "Monday, 1 Dec 2025"
 */
export const formatDateWithDay = (dateString: string | Date): string => {
  return moment.utc(dateString).format('dddd, D MMM YYYY');
};
